'use client';

export default function SalesCopy() {
  return (
    <div className="border-b border-border relative z-10">
      {/* Primary Sales Text – Hero + Intro */}
      <section className="py-24 px-6 md:px-12 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="mb-10 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              Primær salgstekst
            </span>
            <h2 className="text-4xl md:text-5xl text-foreground tracking-tight font-serif font-light mb-8 max-w-4xl">
              Mange virksomheder bruger stadig alt for meget tid på at håndtere dokumenter manuelt.
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-start reveal delay-100">
            <div>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                PDF&apos;er bliver gennemgået, data bliver tastet ind i systemer, og Excel-filer bliver valideret manuelt. Det er tidskrævende, fejlbehæftet og svært at skalere.
              </p>
              <p className="text-xl text-foreground font-serif font-light mb-6">
                Dataflowsuite ændrer den måde, dokumenter bliver håndteret på.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Vi omdanner automatisk jeres dokumenter til struktureret data og validerer det direkte i jeres systemer. Det betyder, at I ikke længere skal bruge tid på manuelle processer – og samtidig får I langt højere datakvalitet.
              </p>
              <p className="text-lg text-foreground font-medium">
                Resultatet er enklere workflows, færre fejl og et langt bedre overblik.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4">
              {[
                { icon: '⏱️', label: 'Tidskrævende processer elimineres' },
                { icon: '✅', label: 'Langt højere datakvalitet' },
                { icon: '📈', label: 'Enklere workflows og bedre overblik' },
              ]?.map((item, i) => (
                <div key={i} className={`p-6 border border-border rounded-lg bg-card flex items-center gap-4 reveal delay-${(i + 1) * 100}`}>
                  <span className="text-3xl">{item?.icon}</span>
                  <p className="text-base text-muted-foreground">{item?.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* Problem → Solution */}
      <section className="py-24 px-6 md:px-12 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              Problem → Løsning
            </span>
            <h2 className="text-4xl md:text-5xl text-foreground tracking-tight font-serif font-light mb-6 max-w-4xl">
              For mange virksomheder ligger udfordringen ikke i systemerne – men i dataene, der kommer ind i dem.
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
              Dokumenter som PDF og Excel er ofte ustrukturerede, og det skaber et ekstra lag af arbejde, før data kan bruges i ERP, økonomisystemer eller andre forretningskritiske platforme.
            </p>
          </div>

          <div className="p-8 border border-primary/20 rounded-lg bg-primary/5 mb-10 reveal delay-100">
            <p className="text-xl text-foreground font-serif font-light mb-2">Det er her Dataflowsuite kommer ind.</p>
            <p className="text-lg text-muted-foreground">
              Vi fungerer som bindeleddet mellem jeres dokumenter og jeres systemer.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 reveal delay-200">
            {[
              { icon: '📖', title: 'Læst korrekt', desc: 'Alle dokumenter læses og fortolkes præcist – uanset format eller struktur.' },
              { icon: '🗂️', title: 'Struktureret ensartet', desc: 'Data normaliseres til et konsistent format, klar til brug i jeres systemer.' },
              { icon: '✔️', title: 'Valideret, før det bruges', desc: 'Ingen fejldata når jeres systemer – alt valideres automatisk inden brug.' },
            ]?.map((item, i) => (
              <div key={i} className={`p-8 border border-border rounded-lg bg-card hover:border-primary/30 transition-colors reveal delay-${(i + 1) * 100}`}>
                <div className="text-4xl mb-4">{item?.icon}</div>
                <h3 className="text-lg text-foreground font-medium mb-2">{item?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-10 p-6 border border-border rounded-lg bg-card/50 reveal delay-300">
            <p className="text-lg text-foreground text-center font-serif font-light">
              Det betyder, at jeres systemer altid arbejder med valide og konsistente data.
            </p>
          </div>
        </div>
      </section>
      {/* Enterprise Angle */}
      <section className="py-24 px-6 md:px-12 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="reveal">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
                Enterprise
              </span>
              <h2 className="text-4xl md:text-5xl text-foreground tracking-tight font-serif font-light mb-6">
                Passer direkte ind i jeres eksisterende setup
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Hvis I allerede har en integrationsplatform som Lobster eller lignende, passer Dataflowsuite direkte ind i jeres eksisterende setup.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                I stedet for at bruge jeres integrationsplatform på at håndtere komplekse dokumenter, kan vi tage os af dokumentbehandlingen og levere rene, strukturerede data ind i jeres flows.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Det reducerer kompleksiteten, forbedrer datakvaliteten og gør jeres integrationer langt mere stabile.
              </p>
              <div className="p-6 border border-primary/30 rounded-lg bg-primary/5">
                <p className="text-xl text-foreground font-serif font-light">
                  I behøver ikke ændre jeres arkitektur – vi forstærker den.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-1 gap-4 reveal delay-200">
              {[
                { icon: '🔗', title: 'Kompatibel med Lobster og lignende', desc: 'Integrerer direkte med eksisterende integrationsplatforme.' },
                { icon: '📉', title: 'Reduceret kompleksitet', desc: 'Vi håndterer dokumenterne – I fokuserer på forretningslogikken.' },
                { icon: '🛡️', title: 'Mere stabile integrationer', desc: 'Rene, validerede data giver færre fejl og mere pålidelige flows.' },
              ]?.map((item, i) => (
                <div key={i} className={`p-6 border border-border rounded-lg bg-card flex items-start gap-4 reveal delay-${(i + 1) * 100}`}>
                  <span className="text-2xl">{item?.icon}</span>
                  <div>
                    <h4 className="text-base text-foreground font-medium mb-1">{item?.title}</h4>
                    <p className="text-sm text-muted-foreground">{item?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
      {/* SMV Angle */}
      <section className="py-24 px-6 md:px-12 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="grid grid-cols-1 gap-4 reveal">
              {[
                { icon: '⚙️', title: 'Vi sætter det op', desc: 'Opsætning, integrationer og drift – vi tager os af det hele.' },
                { icon: '🚀', title: 'Hurtigt i gang', desc: 'Fra manuelle processer til automatiserede workflows uden intern investering.' },
                { icon: '💡', title: 'Ingen teknisk kompleksitet', desc: 'I behøver ikke forstå det tekniske – I mærker bare resultatet.' },
              ]?.map((item, i) => (
                <div key={i} className={`p-6 border border-border rounded-lg bg-card flex items-start gap-4 reveal delay-${(i + 1) * 100}`}>
                  <span className="text-2xl">{item?.icon}</span>
                  <div>
                    <h4 className="text-base text-foreground font-medium mb-1">{item?.title}</h4>
                    <p className="text-sm text-muted-foreground">{item?.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <div className="reveal delay-200">
              <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
                SMV
              </span>
              <h2 className="text-4xl md:text-5xl text-foreground tracking-tight font-serif font-light mb-6">
                Vi gør det for jer
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                For virksomheder uden et eksisterende integrationssetup tilbyder vi en samlet løsning.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                Vi står for opsætning, integrationer og drift, så I ikke skal tænke på det tekniske.
              </p>
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                Det betyder, at I hurtigt kan gå fra manuelle processer til automatiserede workflows uden at skulle investere i interne ressourcer eller komplekse systemer.
              </p>
              <div className="p-6 border border-primary/30 rounded-lg bg-primary/5">
                <p className="text-xl text-foreground font-serif font-light">
                  I får fordelene ved automatisering – uden kompleksiteten.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* User Experience Perspective */}
      <section className="py-24 px-6 md:px-12 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              Brugerperspektiv
            </span>
            <h2 className="text-4xl md:text-5xl text-foreground tracking-tight font-serif font-light mb-6 max-w-3xl">
              Fra jeres perspektiv er det enkelt.
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl leading-relaxed">
              Dokumenter kommer ind i systemet automatisk, bliver konverteret til data og valideret i jeres systemer. Hvis noget ikke stemmer, bliver det fanget med det samme.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 reveal delay-100">
            <div className="p-8 border border-border rounded-lg bg-card">
              <div className="text-4xl mb-6">🔄</div>
              <h3 className="text-xl text-foreground font-medium mb-3">Automatisk dokumentflow</h3>
              <p className="text-muted-foreground leading-relaxed">
                Dokumenter modtages, konverteres og valideres automatisk – uden manuel indgriben fra jeres side.
              </p>
            </div>
            <div className="p-8 border border-border rounded-lg bg-card">
              <div className="text-4xl mb-6">🖥️</div>
              <h3 className="text-xl text-foreground font-medium mb-3">Portal med fuldt overblik</h3>
              <p className="text-muted-foreground leading-relaxed">
                I har en portal, hvor I kan følge alle dokumenter, se status og få fuldt overblik over jeres dataflow. Det giver både kontrol og tryghed i den daglige drift.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Business Impact / Effect */}
      <section className="py-24 px-6 md:px-12 border-b border-border">
        <div className="max-w-7xl mx-auto">
          <div className="mb-12 reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              Effekt
            </span>
            <h2 className="text-4xl md:text-5xl text-foreground tracking-tight font-serif font-light mb-6 max-w-4xl">
              Det virksomheder reelt får, er ikke bare en teknisk løsning – men en forbedring af deres drift.
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 reveal delay-100">
            {[
              { icon: '💰', title: 'Lavere omkostninger', desc: 'Mindre manuelt arbejde betyder direkte besparelser på tid og ressourcer.' },
              { icon: '✅', title: 'Færre fejl', desc: 'Bedre datakvalitet betyder færre fejl og korrektioner i jeres systemer.' },
              { icon: '📈', title: 'Skalerbar automatisering', desc: 'Automatisering betyder, at I kan skalere uden at øge kompleksiteten.' },
              { icon: '🔒', title: 'Data I kan stole på', desc: 'Valide og konsistente data som fundament for jeres forretningsbeslutninger.' },
            ]?.map((item, i) => (
              <div key={i} className={`p-8 border border-border rounded-lg bg-card hover:border-primary/30 transition-colors reveal delay-${(i + 1) * 100}`}>
                <div className="text-4xl mb-4">{item?.icon}</div>
                <h3 className="text-lg text-foreground font-medium mb-2">{item?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{item?.desc}</p>
              </div>
            ))}
          </div>

          <div className="p-8 border border-primary/30 rounded-lg bg-primary/5 text-center reveal delay-300">
            <p className="text-2xl text-foreground font-serif font-light">
              Og vigtigst af alt: I får data, I kan stole på.
            </p>
          </div>
        </div>
      </section>
      {/* Closing Sales Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto text-center">
          <div className="reveal">
            <span className="text-xs font-mono uppercase tracking-widest text-muted-foreground block mb-4">
              Næste skridt
            </span>
            <h2 className="text-4xl md:text-5xl text-foreground tracking-tight font-serif font-light mb-8">
              Uanset om I allerede har et avanceret integrationssetup, eller om I starter fra bunden, kan Dataflowsuite tilpasses jeres behov.
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6 max-w-3xl mx-auto">
              Vi hjælper jer med at automatisere dokumentflows, forbedre datakvaliteten og skabe et stærkere fundament for jeres systemer.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-12 max-w-3xl mx-auto">
              Hvis I vil reducere manuelt arbejde og samtidig få bedre kontrol over jeres data, er det her et naturligt sted at starte.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-4 justify-center reveal delay-200">
            <a
              href="#contact"
              className="px-10 py-5 bg-primary text-primary-foreground rounded-full font-medium hover:opacity-90 transition-opacity text-lg"
            >
              Book demo
            </a>
            <a
              href="mailto:kontakt@dataflowsuite.dk"
              className="px-10 py-5 border border-border text-foreground rounded-full font-medium hover:bg-card/50 transition-colors text-lg"
            >
              Kontakt os
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
